import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Check, Clock, Download, Hourglass, MessageCircle, Send, ThumbsDown, ThumbsUp } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";

const LOCALE_TO_DIR = { ar: "rtl" };

function MessageBubble({ msg, lang, t }) {
  const isCustomer = msg.author === "customer";
  return (
    <div
      className={`flex ${isCustomer ? "justify-end" : "justify-start"}`}
      data-testid={`pq-msg-${msg.author}`}
    >
      <div
        className={[
          "max-w-[80%] rounded-2xl px-4 py-2 text-sm",
          isCustomer
            ? "bg-slate-900 text-white"
            : "border border-slate-200 bg-white text-slate-800",
        ].join(" ")}
      >
        <div className={`mb-1 text-[10px] font-semibold uppercase tracking-wider ${isCustomer ? "text-slate-300" : "text-indigo-600"}`}>
          {isCustomer ? t("quoteReply.byCustomer") : t("quoteReply.bySender")}
        </div>
        <div className="whitespace-pre-wrap">{msg.text}</div>
        <div className={`mt-1 text-[10px] ${isCustomer ? "text-slate-400" : "text-slate-400"}`}>
          {new Date(msg.at).toLocaleString(lang)}
        </div>
      </div>
    </div>
  );
}

export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [decision, setDecision] = useState(null); // "accepted" | "rejected" | "thinking" | null
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { t, i18n } = useTranslation();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API_BASE}/public/quotes/${token}`);
      setQuote(data);
      if (data.pdf_language && data.pdf_language !== i18n.language) {
        i18n.changeLanguage(data.pdf_language);
      }
    } catch (err) {
      const s = err?.response?.status;
      if (s === 410) setError("expired_link");
      else if (s === 404) setError("not_found");
      else setError("unknown");
    } finally {
      setLoading(false);
    }
  }, [token, i18n]);

  useEffect(() => { load(); }, [load]);

  const lang = (quote?.pdf_language || i18n.language || "en").split("-")[0];
  const dir = LOCALE_TO_DIR[lang] || "ltr";

  useEffect(() => {
    document.documentElement.dir = dir;
    return () => { document.documentElement.dir = "ltr"; };
  }, [dir]);

  async function submitDecision() {
    if (!decision) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/quotes/${token}/respond`, {
        decision,
        note: note.trim() || undefined,
      });
      setQuote(data);
      setDecision(null);
      setNote("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      alert(typeof d === "string" ? d : d?.message || "Failed to record response");
    } finally {
      setSubmitting(false);
    }
  }

  async function sendFollowUp() {
    const msg = followUp.trim();
    if (!msg) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/quotes/${token}/message`, {
        message: msg,
      });
      setQuote(data);
      setFollowUp("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      alert(typeof d === "string" ? d : d?.message || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-500" data-testid="public-quote-loading">{t("common.loading")}</div>;
  }
  if (error === "expired_link") {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="public-quote-link-expired">
        <div className="max-w-md rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-amber-600" />
          <h1 className="text-lg font-semibold text-amber-900">{t("publicQuote.linkExpired.title")}</h1>
          <p className="mt-2 text-sm text-amber-800">{t("publicQuote.linkExpired.body")}</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="public-quote-not-found">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <h1 className="text-lg font-semibold text-slate-900">{t("publicQuote.notFound.title")}</h1>
          <p className="mt-2 text-sm text-slate-600">{t("publicQuote.notFound.body")}</p>
        </div>
      </div>
    );
  }

  const isExpired = quote.status === "expired";
  const cr = quote.customer_response;
  const isSettled = ["accepted", "rejected"].includes(quote.status);
  const isThinking = cr && cr.status === "thinking" && !isSettled;
  const canRespond = quote.can_respond;
  const messages = (cr && cr.messages) || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16" data-testid="public-quote-page">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <header className="mb-8 text-center">
          <div className="text-xs uppercase tracking-widest text-slate-500">
            {t(`publicQuote.docType.${lang}`, { defaultValue: t("publicQuote.docType.default") })}
          </div>
          <h1 className="mt-2 font-heading text-4xl tracking-tight text-slate-900" data-testid="public-quote-number">
            {quote.quote_number}
          </h1>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-white">
            <Clock className="h-3 w-3" />
            {t("publicQuote.validUntil")}: {formatDate(quote.expires_at, lang)}
          </div>
        </header>

        {isExpired && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="public-quote-expired-banner">
            <div className="flex items-center gap-2 font-semibold"><AlertCircle className="h-4 w-4" /> {t("publicQuote.expiredTitle")}</div>
            <p className="mt-1 text-amber-800">{t("publicQuote.expiredBody")}</p>
          </div>
        )}

        {isSettled && cr && (
          <div
            className={`mb-6 rounded-2xl border p-4 text-sm ${
              cr.status === "accepted"
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-red-200 bg-red-50 text-red-900"
            }`}
            data-testid="public-quote-settled-banner"
          >
            <div className="font-semibold">
              {t("publicQuote.settled." + cr.status)} — {new Date(cr.responded_at).toLocaleString()}
            </div>
            {cr.note && (
              <p className="mt-2 whitespace-pre-wrap">{cr.note}</p>
            )}
          </div>
        )}

        {isThinking && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="public-quote-thinking-banner">
            <div className="flex items-center gap-2 font-semibold">
              <Hourglass className="h-4 w-4" /> {t("quoteReply.customerTyping")}
            </div>
          </div>
        )}

        {/* Message thread (only shown when a conversation has started) */}
        {messages.length > 0 && (
          <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="public-quote-thread">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <MessageCircle className="h-4 w-4 text-indigo-600" /> {t("quoteReply.threadTitle")}
            </div>
            <div className="space-y-2">
              {messages.map((m, i) => (
                <MessageBubble key={i} msg={m} lang={lang} t={t} />
              ))}
            </div>
            {canRespond && (
              <div className="mt-4 flex items-end gap-2">
                <textarea
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                  placeholder={t("quoteReply.customerReplyPlaceholder")}
                  rows={2}
                  data-testid="public-quote-followup-input"
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-slate-400 focus:outline-none"
                />
                <button
                  onClick={sendFollowUp}
                  disabled={submitting || !followUp.trim()}
                  data-testid="public-quote-followup-send"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> {t("quoteReply.sendMessage")}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">{t("publicQuote.to")}</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{quote.customer?.name}</div>
              <div className="text-sm text-slate-600">{quote.customer?.email}</div>
            </div>
            <div className="sm:text-end">
              <div className="text-xs uppercase tracking-wide text-slate-500">{t("publicQuote.total")}</div>
              <div className="mt-1 text-3xl font-bold tracking-tight text-slate-900 tabular-nums" data-testid="public-quote-total">
                {formatCurrency(quote.total, quote.currency, lang)}
              </div>
              <div className="text-xs text-slate-500">{t("publicQuote.issued")}: {formatDate(quote.issue_date, lang)}</div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-slate-100">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-start">{t("publicQuote.item")}</th>
                  <th className="px-4 py-2 text-end">{t("publicQuote.qty")}</th>
                  <th className="px-4 py-2 text-end">{t("publicQuote.unitPrice")}</th>
                  <th className="px-4 py-2 text-end">{t("publicQuote.lineTotal")}</th>
                </tr>
              </thead>
              <tbody>
                {quote.line_items.map((li, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{li.name}</div>
                      {li.description && <div className="text-xs text-slate-500">{li.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-end tabular-nums">{li.quantity} {li.unit || ""}</td>
                    <td className="px-4 py-3 text-end tabular-nums">{formatCurrency(li.unit_price, quote.currency, lang)}</td>
                    <td className="px-4 py-3 text-end font-medium tabular-nums">{formatCurrency(li.total, quote.currency, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {quote.notes && (
            <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-wrap">
              {quote.notes}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <a
              href={`${API_BASE}/public/quotes/${token}/pdf?lang=${lang}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="public-quote-pdf"
            >
              <Download className="h-4 w-4" /> {t("publicQuote.downloadPdf")}
            </a>

            {canRespond && !decision && (
              <div className="flex flex-wrap gap-2" data-testid="public-quote-actions">
                <button
                  onClick={() => setDecision("accepted")}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                  data-testid="public-quote-accept"
                >
                  <ThumbsUp className="h-4 w-4" /> {t("publicQuote.accept")}
                </button>
                <button
                  onClick={() => setDecision("rejected")}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                  data-testid="public-quote-reject"
                >
                  <ThumbsDown className="h-4 w-4" /> {t("publicQuote.reject")}
                </button>
                {!isThinking && (
                  <button
                    onClick={() => setDecision("thinking")}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    data-testid="public-quote-thinking"
                  >
                    <Hourglass className="h-4 w-4" /> {t("publicQuote.thinking")}
                  </button>
                )}
              </div>
            )}
          </div>

          {decision && (
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="public-quote-decision-modal">
              <div className="text-sm font-semibold text-slate-900">
                {t(`publicQuote.confirm.${decision}`)}
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("publicQuote.notePlaceholder")}
                rows={3}
                data-testid="public-quote-note"
                className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-slate-400 focus:outline-none"
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() => { setDecision(null); setNote(""); }}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={submitDecision}
                  disabled={submitting}
                  data-testid="public-quote-submit"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  <Check className="h-4 w-4" /> {t("publicQuote.confirmSend")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
